/**
 * Money formatting — one implementation, used everywhere AUD is shown.
 *
 * This existed in three places before (InvoicesPage, FleetInvoicesPage and the phone
 * app), each with its own `Intl.NumberFormat` call and its own idea of what a missing
 * amount looks like. They had drifted: one rendered null as "$0.00" and another as an
 * em dash.
 *
 * ⚠️ That difference is REAL and is preserved deliberately — it is now a named
 * argument rather than a property of which file you happen to be in. "$0.00" and
 * "not computed" are different facts, and on a customer-facing screen showing the
 * second as the first tells someone they owe nothing.
 */

/** One formatter instance — constructing Intl.NumberFormat per call is the slow path. */
const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

/**
 * Format an amount as AUD.
 *
 * @param {number|string|null|undefined} amount
 * @param {{ fallback?: string }} [options] what to render when the amount is absent
 *   or unparseable. Defaults to an em dash — the safe choice on anything a customer
 *   reads. Pass `'$0.00'` only where zero is genuinely the right reading.
 */
export const formatAud = (amount, { fallback = '—' } = {}) => {
    if (amount === null || amount === undefined || amount === '') return fallback;

    const n = Number(amount);
    if (Number.isNaN(n)) return fallback;

    return AUD.format(n);
};

/**
 * AUD with a missing amount rendered as zero.
 *
 * Kept as its own export so the call sites that want this read as a deliberate
 * choice, and so they are greppable if that choice is ever revisited.
 */
export const formatAudOrZero = (amount) => AUD.format(Number(amount) || 0);
